import { z } from "zod";
import { seatReservationSchema } from "./seat-reservation.js";
/**
 * `GET /api/v1/lobby` — the lobby read model (phase plan S3-3).
 *
 * **This shape is Ivy's §16 "what the tile needs from the read model", LOCKED.**
 * Her words: "anything missing here is a design that cannot render". Fields are
 * added additively; nothing is renamed or removed without a design pass.
 *
 * ── The two rulings that are easiest to undo by accident ────────────────────
 *
 * 1. **`viewer.balanceCoins` is a `number | null`, and there is NO server-side
 *    affordability boolean** (Ivy §9.1, her one-truth rule). The coin chip
 *    renders the number; the tile derives "need 12 more" from the same number.
 *    A boolean and a number can disagree, and a chip showing `—` above a tile
 *    asserting a shortfall is an incoherent screen. This refines the phase
 *    plan's S3-3 wording ("the caller's affordability flag") — she is right and
 *    the flag is not shipping.
 *
 * 2. **`—` means unknown, `0` means zero, and they are different statements.**
 *    In Sprint 3 `balanceCoins` is ALWAYS `null`: no ledger exists until S4-1,
 *    so no tile may enter the `locked` state and no claim is made about what a
 *    player can afford. The shape ships now; the value arrives at S4.
 */
/** `guest` and `registered` are the only two the design branches on (§6.4). */
export const lobbyViewerRoleSchema = z.enum(["guest", "registered"]);
export const lobbyViewerSchema = z.object({
    role: lobbyViewerRoleSchema,
    /**
     * `null` until a balance source exists (S4-1). NEVER a stand-in `0` — a guest
     * with a real, empty wallet sees `0`; a client with no balance source sees
     * `—`. Substituting one for the other is a lie about a number.
     */
    balanceCoins: z.number().int().nullable(),
});
export const lobbyMetaSchema = z.object({
    /**
     * `flags.shop_enabled`. Drives the coin chip's `live-inert` vs `live` state
     * (§9.2) and whether a locked tile gets a Shop button (§6.4). `false` until
     * S7 — flipping it early is a regression, not a feature.
     */
    shopEnabled: z.boolean(),
    /** ISO-8601. The client's stale-data check and greeting bucket read it. */
    serverTime: z.string(),
});
/**
 * Design tokens a `game_types.color` may name (Ivy §5.3).
 *
 * The wire carries a **token key, never hex** — that is the LOCK, and it is
 * what keeps "no raw hex outside packages/ui" true for data-driven colour. The
 * schema is deliberately a plain string rather than an enum: an operator adding
 * a token the client does not know must produce Ivy's documented fallback
 * (`--wr-play`, logged once), not a 500 from response serialisation.
 */
export const KNOWN_COLOR_TOKENS = ["play", "coin", "good", "warn", "lilac", "sand"];
export const lobbyGameTypeSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    /** From the database, rendered as-is. NOT an i18n key (Ivy §15). */
    name: z.string(),
    /** Fallback for the ART-SLOT glyph, e.g. `WS`. */
    monoLabel: z.string(),
    colorToken: z.string(),
    /** ART-SLOT id, e.g. `wr-gametype-word-search`. See `artSlotIdForTypeSlug`. */
    artRef: z.string(),
    sortOrder: z.number().int(),
});
export const lobbyGameModeSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    /** The tile's mode label and the matrix column header. Database content. */
    name: z.string(),
    /** The seat shape (`Solo duel`, `Pairs`). Rendered in the matrix header, not on the tile face. */
    subLabel: z.string().nullable(),
    maxPlayers: z.number().int(),
    sortOrder: z.number().int(),
});
/**
 * A seat that is already taken.
 *
 * `colorIndex` (0–5) into the S2 avatar colour rotation, **never a player id**
 * (Ivy §7). A player's colour is a stable hash of `players.id` computed
 * server-side; only the index crosses the wire. The lobby does not need, and
 * must not receive, other players' identifiers.
 */
export const lobbySeatPreviewSchema = z.object({
    initials: z.string(),
    colorIndex: z.number().int().min(0).max(5),
    /**
     * **The seat's avatar art — absolute, server-resolved; `null` = no art**
     * (S11 spec 1, RATIFIED).
     *
     * ── Required-and-nullable, never optional ─────────────────────────────────
     *
     * An optional field lets a producer omit it and leaves a reader unable to
     * distinguish *"no art for this seat"* from *"an older build that did not
     * publish the field"*. Nullable forces every producer to answer. Additive to
     * the response, so no existing reader breaks.
     *
     * ── The sentinel convention this field MINTS ──────────────────────────────
     *
     * A Colyseus schema primitive is always defined, so `LobbySeat.avatarUrl`
     * carries `""` for absent and `lobbyBodyFromState` maps `"" → null` at the
     * boundary — one place, like `roomId` and `subLabel` before it.
     * **This sentinel-at-schema / null-at-contract convention is minted here and
     * is reused by `sideIndex` (spec 3, `-1 → null`). There is no third pattern.**
     *
     * ── Resolution is server-side. Always. ───────────────────────────────────
     *
     * The URL is absolutised by `apps/game`'s `toAbsoluteAvatarUrl` — the same
     * rule the room-join seat payload gets — before it reaches the wire. The
     * client never composes, defaults or repairs an avatar URL: a client that can
     * build one can build a wrong one, and the failure is silent (an SPA shell
     * answers `200 text/html` for a stray path, so the `<img>` does not 404
     * loudly — it decodes as not-an-image and drops to the monogram).
     *
     * ── Privacy — LOCKED ──────────────────────────────────────────────────────
     *
     * This URL is the **only** identity-adjacent value that crosses. `players.id`
     * still never does (Ivy §7, untouched). It is licensed because avatar art is
     * a bounded preset + unlock bucket over a public choice, shown to the same
     * people who are in the room, and the seat already carries initials. **If
     * avatars ever become player-uploaded or per-player-unique, the URL becomes
     * an identifier and this field returns to a ruling before that ships.**
     *
     * ── Muted states are a CLIENT rule, not a wire rule ───────────────────────
     *
     * `locked` / `left` / `inactive` tiles render no art (Ivy, LOCKED) — and the
     * field still ships in those states. Withholding it server-side would make
     * the field's presence state-dependent and make the seat comparator lie.
     */
    avatarUrl: z.string().nullable(),
});
/* ==========================================================================
   The four room-describing numbers — ONE object, ONE projection per path
   ========================================================================== */
/**
 * **`RoomFacts` — what the room this tile points at will actually cost, pay and
 * run** (S11 spec 2).
 *
 * Ivy's binding requirement is that all four room-describing numbers come from
 * **one side**. The mechanism that makes that enforceable is this type:
 *
 * > **The four numbers travel as ONE object, produced by ONE projection per
 * > path, and no caller composes any of them individually.**
 *
 * Two projections exist and there may never be a third:
 *
 *  1. **Live path** — `apps/game`'s `roomFactsFromSnapshot`, over the room's own
 *     `rooms.*_snapshot` columns and its resolved timing. The numbers a tile
 *     shows are then the numbers the room it points at will actually run, which
 *     is what stops a tile promising three rounds to a room spawned to run five.
 *  2. **Fallback path** — `@wr/config`'s `roomFactsFromContext`, over the same
 *     `ConfigContext` today's `entryFeeCoins` and `prizeCoins` already come
 *     from. A third provenance is not permitted.
 *
 * `lobbyTileSchema` spreads this shape rather than re-listing the four fields,
 * so "the tile's four numbers are exactly `RoomFacts`" is a fact about the
 * types and not a comment somebody has to keep true.
 *
 * ── Why none of the four is `.positive()` here, though all four are ─────────
 *
 * `entryFeeCoins` and `prizeCoins` have never been, and the two new ones match
 * them exactly — which is what spec 2 asks for on the fallback path and is also
 * the safe reading. `roundSeconds` derives from `timing.round_ms`, which is
 * operator-editable (platform floor `1`, and a per-config `timing_overrides`
 * entry is accepted at `>= 0`), so a `.positive()` here would turn one bad
 * config edit into a failed response schema and a **500 for the whole lobby** —
 * exactly what `buildLobbyBody`'s LOCKED runtime posture forbids (*"a bad edit
 * must cost one tile, never the screen"*). Positivity is asserted on the
 * projections instead, where a violation is a test failure rather than an
 * outage.
 */
export const roomFactsSchema = z.object({
    entryFeeCoins: z.number().int(),
    /**
     * The **resolved** prize (`resolvePrize`), boost applied. `0` on the free tile
     * is the launch value, not a missing number: Ivy's `lobby.prize.none` branch
     * is the path that ships (Juno Ruling #2). Never render "PRIZE 0".
     */
    prizeCoins: z.number().int(),
    /** `room_configs.rounds` / `rooms.rounds_snapshot`. Ivy's `{rounds}` copy token. */
    rounds: z.number().int(),
    /**
     * The resolved round length in **seconds** — Ivy's `{seconds}` copy token.
     *
     * Seconds rather than the milliseconds every internal timing value uses,
     * because the copy slot is seconds and a client that divided would be the
     * second place the unit is decided. Rounded once, in the projections.
     *
     * **LOCKED — no total-duration estimate.** Two config facts stated plainly are
     * honest; a total ("≈5 min") is a promise that includes fill time and the
     * results screen, and it is the live-line defect in a new costume.
     */
    roundSeconds: z.number().int(),
});
/** `rooms.phase` (architecture §5.4). Always `filling` in S3 — no rooms exist yet. */
export const lobbyTilePhaseSchema = z.enum([
    "filling",
    "ready_check",
    "in_progress",
    "completed",
    "cancelled",
]);
export const lobbyTileSchema = z.object({
    roomConfigId: z.string().uuid(),
    /**
     * The id of the room this tile is **currently showing** — Ivy §16 note 3b,
     * added 2026-07-29 (lobby spec §20.2).
     *
     * It is the same room whose `playerCount`, `phase` and `seatPreviews` are on
     * the tile, and it changes with them. `null` only in the window before a
     * config's first room exists; `null` means "no id to compare", never "not
     * left".
     *
     * **The id space is `rooms.id` — ours, not Colyseus's.** Ivy's note asks for
     * "the identifier the client will route on", and the two candidate spaces are
     * disjoint (T-208 N5). `rooms.id` is the one every *comparison* is made in:
     * the leave response returns it, the room state carries it, and the 409
     * `seat_closed` body names it — so a spent-room set keyed on it can never
     * fail to match. The `/room/:roomId` **URL** carries the Colyseus id, because
     * that is the only id a reservation names before a socket exists and nothing
     * compares the URL. One comparison space, one routing convenience, no
     * translation table. Flagged to Ivy/Dex in T-210.
     *
     * **LOCKED — never rendered** (lobby §20.2): not as visible text, not in an
     * accessible name, not in a `title`. A room id in a network payload is
     * correct; a room id in the DOM as text is a finding.
     */
    roomId: z.string().nullable(),
    gameTypeId: z.string().uuid(),
    gameModeId: z.string().uuid(),
    /**
     * **The four room-describing numbers, spread from `roomFactsSchema`.**
     *
     * Spread rather than re-listed so the tile cannot gain a fifth number, or
     * lose one, without the projections moving with it — Ivy's "all four come
     * from one side" expressed in the type rather than in a comment.
     */
    ...roomFactsSchema.shape,
    playerCount: z.number().int(),
    maxPlayers: z.number().int(),
    phase: lobbyTilePhaseSchema,
    seatPreviews: z.array(lobbySeatPreviewSchema),
    /**
     * Post-v1. Always `null` in v1 — there is no `room_config_boosts` table
     * (§5.2's forward provision) and nothing seeds one. Typed rather than
     * hard-`null` so the day it lands, the contract does not move.
     */
    boost: z.object({ multiplierBps: z.number().int(), endsAt: z.string() }).nullable(),
    sortOrder: z.number().int(),
});
/**
 * The **viewer-independent** half of the response: everything that is the same
 * for every caller at the same instant.
 *
 * This is what the api caches under one Redis key, and — from S3-4 — what the
 * `LobbyRoom` mirrors into Colyseus state. `viewer` and `meta.serverTime` are
 * attached per request/per socket precisely because caching a per-player field
 * under a shared key is how one player sees another's balance.
 */
export const lobbyBodySchema = z.object({
    shopEnabled: z.boolean(),
    types: z.array(lobbyGameTypeSchema),
    modes: z.array(lobbyGameModeSchema),
    tiles: z.array(lobbyTileSchema),
});
export const lobbyResponseSchema = z.object({
    viewer: lobbyViewerSchema,
    meta: lobbyMetaSchema,
    /**
     * Only types that have at least one tile.
     *
     * Ivy §16 note 5 derives `activeTypeCount` from "types[] that have ≥1 tile",
     * because a type with no tiles does not render (§3.3) and must not tip the
     * desktop into the matrix arrangement on its own. Filtering server-side makes
     * that derivation `types.length` and makes the failure mode she names
     * unrepresentable rather than merely avoided. Flagged to Ivy in T-186.
     */
    types: z.array(lobbyGameTypeSchema),
    /** Only modes that have at least one tile — same reasoning; empty columns have no header. */
    modes: z.array(lobbyGameModeSchema),
    /** Active, valid cells only. Sorted type → mode → config (Ivy §3.3). */
    tiles: z.array(lobbyTileSchema),
});
/* ==========================================================================
   The lobby's Redis surface — one channel, one cache key, one event shape
   ========================================================================== */
/**
 * Moved here from `apps/api/src/lobby/cache.ts` in S3-4 (T-188), because a
 * second service now needs these names.
 *
 * `apps/api` publishes on the channel; `apps/game`'s `LobbyRoom` subscribes to
 * it. A channel name is a contract between two processes in exactly the way a
 * request body is, and two hand-typed `"wr:lobby"` string literals is a
 * silently-dead subscription the day one of them gains a typo or a version
 * suffix. T-188's ticket says it in one line: **if `apps/game` needs them, MOVE
 * them to `@wr/contracts`, don't re-type the strings.**
 *
 * The cache *behaviour* (read/write/invalidate, the never-throw posture) stays
 * in the api — it owns the cache. Only the names and the wire event are shared.
 */
/** The Redis key holding the viewer-independent lobby body. Owned by `apps/api`. */
export const LOBBY_CACHE_KEY = "wr:lobby:body:v1";
/**
 * The cache's TTL backstop, in seconds.
 *
 * NOT the invalidation mechanism — the Postgres `NOTIFY` listener is. This is
 * what keeps the 5-second DoD unconditional when a notification is lost.
 */
export const LOBBY_CACHE_TTL_SECONDS = 3;
/**
 * The fan-out channel. **Two publishers, one channel** — sanctioned by
 * architecture §16e A22 and re-affirmed by LOCKED rule 25:
 *
 *  - `apps/api` publishes `config.changed` from the system's **one** Postgres
 *    `LISTEN` connection, after deleting the cache key.
 *  - `apps/game`'s `RoomOrchestrator` (S4-3) publishes `rooms.changed` when it
 *    spawns or reaps a room.
 *
 * A second *Postgres* listener is what rule 25 forbids, not a second publisher.
 */
export const LOBBY_CHANNEL = "wr:lobby";
export const lobbyCacheEventSchema = z.object({
    /**
     * `config.changed` — a configuration table changed; every subscriber re-reads
     * the read model from Postgres.
     *
     * `rooms.changed` — a live room appeared or went away (S4-3). Occupancy only:
     * subscribers refresh from `matchMaker.query()` and do **not** touch Postgres,
     * which is what keeps a busy lobby from turning room churn into database load.
     *
     * Same shape for both, and the payload deliberately carries **no row data**
     * (§16e A22 point 2): `pg_notify` caps at 8000 bytes, and a payload that
     * looked like data would eventually be treated as data.
     */
    type: z.enum(["config.changed", "rooms.changed"]),
    /** What triggered it — a table name, or `orchestrator:<reason>`. */
    source: z.string(),
    at: z.string(),
});
/**
 * The room name `LobbyRoom` is registered under, and the only `roomName` the
 * api may ask the game service to reserve a lobby seat in.
 */
export const LOBBY_ROOM_NAME = "lobby";
/**
 * `POST /api/v1/lobby/watch` — a seat in the live lobby room (S3-4).
 *
 * Deliberately **not** shaped like `{ reservation, redirected }`: a lobby seat
 * cannot be redirected. There is one lobby room per process and the reservation
 * names the process that will serve it, so a `redirected` flag here would be a
 * field that is structurally always `false` — and A11's rule is that clients
 * must treat `redirected` as required, which only stays meaningful if the field
 * appears where redirection is actually possible.
 *
 * As with `/dev/join`, the route attaches **no Fastify response schema**: a
 * compiled serialiser drops unknown keys, and the reservation must reach
 * `consumeSeatReservation()` exactly as Colyseus wrote it (§16 A8).
 */
export const lobbyWatchResponseSchema = z.object({
    reservation: seatReservationSchema,
});
/**
 * The `_` → `-` slug transform, in one place, named — §16d A21.1's requirement
 * verbatim.
 *
 * DB slugs are `snake_case` (`word_search`); Ivy's ART-SLOT ids hyphenate
 * (`wr-gametype-word-search`). Applied **server-side** in the read model so the
 * wire carries a finished slot id: A21.1 assigned the transform to the client,
 * but "one transform in one place" is better served by the one place that
 * cannot be forked across a web client, an admin UI and a future native app.
 * It is exported here so any client that needs to rebuild an id uses the same
 * function rather than a second `replace`.
 */
export function artSlotIdForTypeSlug(slug) {
    return `wr-gametype-${slug.replace(/_/g, "-")}`;
}
//# sourceMappingURL=lobby.js.map